import * as dot from 'dot-object';
import { observe } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Button, Col, Input, Label, Row } from 'reactstrap';
import FormGroup from 'reactstrap/lib/FormGroup';
import ButtonRow from 'src/Components/ButtonRow';
import OpenArrow from 'src/Components/OpenArrow/OpenArrow';
import { Role } from 'src/Interfaces/AdminInterface';
import Permissions from 'src/Services/Permission/Permissions';
import { AdminStore } from 'src/Stores/Admin/AdminStore';
import { MessageStore } from 'src/Stores/Messages/Messages';
import TenantStore from 'src/Stores/Tenant/TenantStore';
import { UserStore } from 'src/Stores/User/UserStore';

const recursePermTree = (currLevelBase: any, currLevelPerm: any, key: string) => {
    if(typeof currLevelPerm[key] === 'object') {
        let isSelected = false;
        let wentDeeper = false;
        for (const innerKey in currLevelPerm[key]) {
            if (currLevelPerm[key].hasOwnProperty(innerKey)) {
                if (key === 'Admin' && innerKey === 'View') {
                    continue;
                }
                if (key === 'Wells' 
                        && (innerKey === 'Create' 
                            || innerKey === 'IsFieldSupervisor'
                            || innerKey === "Archive"
                            || innerKey === "NotifyNew"
                            || innerKey === "ViewArchive"
                            || innerKey === "NotifyFinish")
                    ) {
                    continue;
                }
                if (key === 'WellTests' 
                    && (innerKey === 'EditSpecificGravity'
                        || innerKey === 'EditIntervalStartDate'
                        || innerKey === 'EditProductPrice'
                        || innerKey === 'EditAdjustedAmount')
                ) {
                    continue;
                }
                if (key === 'Dispatch'
                        && (innerKey === 'Confirm'
                            || innerKey === 'Comment'
                            || innerKey === 'Allocate'
                            || innerKey === 'IsDispatcher'
                            || innerKey === 'UpdateConfirmed')
                    ) {
                        continue;
                }

                if (typeof currLevelPerm[key][innerKey] === 'boolean') {
                    if (currLevelPerm[key][innerKey]) {
                        isSelected = true;
                    }
                } else {
                    wentDeeper = true;
                    recursePermTree(currLevelBase[key], currLevelPerm[key], innerKey);
                }
            }
        }
        if (!wentDeeper) {
            currLevelBase[key] = isSelected;
        }
    }
};

const checkPermsRecurse = (currLevelBase: any, currLevelPerm: any, key: string): boolean => {
    if (typeof currLevelBase[key] === 'object') {
        for (const innerKey in currLevelBase[key]) {
            if (currLevelBase[key].hasOwnProperty(innerKey)) {
                if (!checkPermsRecurse(currLevelBase[key], currLevelPerm[key], innerKey)) {
                    return false;
                }
            }
        }
    } else if(typeof currLevelBase[key] === 'boolean' && currLevelBase[key]) {
        const currPerm = currLevelPerm[key];
        for (const permKey in currPerm) {
            if (currPerm.hasOwnProperty(permKey)) {
                if (currPerm[permKey]) {
                    return true;
                }
            }
        }
        return false;
    }
    return true;
};

@inject('stores')
@observer
class AdminRoleDetails extends React.Component {

    public props: any;
    private roleDisposer: any;
    private AdminStore: AdminStore;
    private MessageStore: MessageStore;
    private UserStore: UserStore;
    private TenantStore: TenantStore;
    
    public state = {
        basePermissions: {
            Admin: {
                Categories: false,
                Contractors: false,
                InternalEmails: false,
                Inventory: false,
                Operators: false,
                OptionalFieldLists: {
                    IntervalTypeOptions: false,
                    MudPropertyOptions: false,
                    MudTestOptions: false,
                    OilFluidSubTypeOptions: false,
                    StorageTypeOptions: false,
                    SubIntervalTypeOptions: false,
                    TargetFormationOptions: false,
                    ToolsDescriptionOptions: false,
                    WaterFluidSubTypeOptions: false,
                },
                RegionInventory: false,
                Roles: false,
                SolidEquip: false,
                TaxRates: false,
                UnitOfMeasure: false,
                Users: false,
                View: false,
                Warehouses: false,
            },
            Dispatch: {
                Allocate: false,
                Approve: false,
                Confirm: false,
                Create: false,
                Notifications: false,
                Read: false,
                Reject: false,
                UpdateApproved: false,
                UpdateConfirmed: false,
                UpdateRTA: false,
                UpdateSubmitted: false,
            },
            Reports: {
                Custom: {
                    View: false,
                },
                System: {
                    View: false,
                }
            },
            WellTests: {
                Approve: false,
                Create: false,
                Delete: false,
                Read: false,
                Update: false,
            },
            Wells: {
                ActivateSupervisor: false,
                AddUser: false,
                Close: false,
                Create: false,
                DeactivateSupervisor: false,
                Edit: false,
                NotifyFinish: false,
                NotifyNew: false,
                Read: false,
                ReadClosed: false,
                RemoveUser: false,
            },
        },
        permissions: undefined as any,
        role: new Role(),
        userPermissionAccordian: {
            dispatch: false,
            reports: false,
            wellTests: false,
            wells: false,
        },
    };

    constructor(props: any) {
        super(props);
        this.AdminStore = props.stores.AdminStore;
        this.MessageStore = props.stores.MessageStore;
        this.UserStore = props.stores.UserStore;
        this.TenantStore = props.stores.TenantStore;
    }

    private checkPermissionsLoad = async () => {
        if(!Permissions.PermissionsLoaded()) {
            return this.AdminStore.getAllPermissions()
                .then(data => {
                    if(data) {
                        Permissions.LoadAllPermissions(data);
                    }
                });
        }
    }
    public componentDidMount() {
        this.createDisposers();
        const roleId = this.props.match.params.id;
        this.checkPermissionsLoad();
        if (roleId === 'new') {
            this.AdminStore.createNewRole();
        } else {
            this.AdminStore.getRoleById(roleId);
        }
    }

    public componentWillUnmount() {
        this.roleDisposer();
    }

    private getBasePermissions = (currPerms: any) => {
        const basePermssions = this.state.basePermissions;
        for (const key in basePermssions) {
            if (basePermssions.hasOwnProperty(key)) {
                recursePermTree(basePermssions, currPerms, key);
            }
        }
        return basePermssions;
    }

    public createDisposers = () => {
        this.roleDisposer = observe(this.AdminStore, 'role', change => {
            this.checkPermissionsLoad().then(() => {
                const currPermissions = Permissions.AssignPermissions(change.newValue.permissions);
                const basePermissions = this.getBasePermissions(currPermissions.Permissions);
                this.setState({
                    basePermissions,
                    permissions: currPermissions.Permissions,
                    role: change.newValue,
                });
            });
        });
    }

    public handleChange = (event: any) => {
        const role = this.state.role;
        role[event.target.name] = event.target.value;
        this.setState({
            role
        });
    }

    private isRoleComplete = () => {
        const role = this.state.role;
        return role.name !== '' && this.hasOnePermission();
    }

    private hasOnePermission = () => {
        return Permissions.TransformPermissionsToString(this.state.permissions).length > 0;
    }

    private checkPermsSelected = () => {
        const basePerms = this.state.basePermissions;
        const perms = this.state.permissions;

        for (const key in basePerms) {
            if (basePerms.hasOwnProperty(key)) {
                if (!checkPermsRecurse(basePerms, perms, key)) {
                    return false;
                }
            }
        }
        return true;
    }
    private handleSaveRole = () => {
        const needWarning = !this.checkPermsSelected();
        let areYouSure = true;
        if (needWarning) {
            areYouSure = confirm("Not all permissions currently viewed have a radio selection.\n\nAre you sure you want to save?\n\nPress OK to continue.");
        }

        if (areYouSure) {
            const role = this.state.role;
            role.permissions = Permissions.TransformPermissionsToString(this.state.permissions);
            this.AdminStore.saveRole(role)
            .then(data => {
                if (data) {
                    this.AdminStore.getRoles();
                    this.props.history.push('/admin/roles');
                    this.MessageStore.addMessage('Role successfully saved.', 'success');
                }
            });
        }
    }

    public handleBaseChange = (event: any) => {
        const basePermissions = this.state.basePermissions;
        const permissions = this.state.permissions;
        const currVal = dot.pick(event.target.name, basePermissions);
        dot.str(event.target.name, !currVal, basePermissions);
        if (currVal) {
            const permLevel = dot.pick(event.target.name, permissions);
            for (const key in permLevel) {
                if (permLevel.hasOwnProperty(key)) {
                    permLevel[key] = false;
                }
            }
        }
        this.setState({
            basePermissions,
            permissions,
        });
    }

    public handlePermissionsChange = (inclusive?: boolean) => (event: any) => {
        const permissions = this.state.permissions;
        const permPath = event.target.name + event.target.value;
        const parentPath = event.target.name;
        const parentObj = dot.pick(parentPath, permissions);
        if (!inclusive) {
            for (const key in parentObj) {
                if (parentObj.hasOwnProperty(key)) {
                    if (typeof parentObj[key] === 'boolean') {
                        parentObj[key] = false;
                    }
                }
            }
        }
        const currVal = dot.pick(permPath, permissions);
        dot.str(permPath, !currVal, permissions);
        if (
            event.target.value !== '.Read'
            && event.target.value !== '.View'
            && !currVal
            && parentPath.includes('Admin')
            ) {

            parentObj.Read = true;
        }
        this.setState({
            permissions,
        });
    }

    private toggleList = (toggleName:string) => () => {
        const userPermissionAccordian = this.state.userPermissionAccordian;
        userPermissionAccordian[toggleName] = !userPermissionAccordian[toggleName];
        this.setState({
            userPermissionAccordian
        });
    }

    private renderOptionalFieldListPermission = (permName: string, permDescription: string) => {
        const basePermRef = this.getReferenceFromDotFormat(this.state.basePermissions, permName);
        const permRef = this.getReferenceFromDotFormat(this.state.permissions, permName);
        return (
        <React.Fragment>
            <FormGroup check={true}>
                <Label check={true}>
                    <Input
                        type="checkbox"
                        name={permName}
                        checked={basePermRef}
                        onChange={this.handleBaseChange}
                        />{permDescription}
                </Label>
            </FormGroup>
            { basePermRef && 
                <div style={{marginLeft: '2rem'}}>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Read}
                                value=".Read"
                                onChange={this.handlePermissionsChange(true)}
                                />Read
                        </Label>
                    </FormGroup>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Update}
                                value=".Update"
                                onChange={this.handlePermissionsChange(true)}
                                />Update
                        </Label>
                    </FormGroup>
                </div>
            }
        </React.Fragment>
        );
    }

    private renderDispatchNotificationSingle = (permName: string, basePermName: string, permRef: any, permDescription: string) => {
        return (
            <FormGroup check={true}>
                <Label check={true}>
                    <Input
                        type="checkbox"
                        name={basePermName}
                        checked={this.state.permissions && permRef[permName]}
                        value={`.${permName}`}
                        onChange={this.handlePermissionsChange(true)}
                        />{permDescription}
                </Label>
            </FormGroup>
        );
    }
    private renderDispatchNotificationsPermissions = () => {
        const basePermRef = this.getReferenceFromDotFormat(this.state.basePermissions, 'Dispatch.Notifications');
        const permRef = this.getReferenceFromDotFormat(this.state.permissions, 'Dispatch.Notifications');
        return (
        <React.Fragment>
            <FormGroup check={true}>
                <Label check={true}>
                    <Input
                        type="checkbox"
                        name={"Dispatch.Notifications"}
                        checked={basePermRef}
                        onChange={this.handleBaseChange}
                        />Dispatch Notifications
                </Label>
            </FormGroup>
            { basePermRef && 
                <div style={{marginLeft: '2rem'}}>
                    { this.renderDispatchNotificationSingle('Submit', 'Dispatch.Notifications', permRef, 'Submit')}
                    { this.renderDispatchNotificationSingle('Reject', 'Dispatch.Notifications', permRef, 'Reject')}
                    { this.renderDispatchNotificationSingle('Approve', 'Dispatch.Notifications', permRef, 'Approve')}
                    { this.renderDispatchNotificationSingle('Deny', 'Dispatch.Notifications', permRef, 'Deny')}
                    { this.renderDispatchNotificationSingle('Confirm', 'Dispatch.Notifications', permRef, 'Confirm')}
                </div>
            }
        </React.Fragment>
        );
    }
    private renderAdminPermission = (permName: string, permDescription: string) => {
        const basePermRef = this.getReferenceFromDotFormat(this.state.basePermissions, permName);
        const permRef = this.getReferenceFromDotFormat(this.state.permissions, permName);
        return (
        <React.Fragment>
            <FormGroup check={true}>
                <Label check={true}>
                    <Input
                        type="checkbox"
                        name={permName}
                        checked={basePermRef}
                        onChange={this.handleBaseChange}
                        />{permDescription}
                </Label>
            </FormGroup>
            { basePermRef && 
                <div style={{marginLeft: '2rem'}}>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Create}
                                value=".Create"
                                onChange={this.handlePermissionsChange(true)}
                                />Create
                        </Label>
                    </FormGroup>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Read}
                                value=".Read"
                                onChange={this.handlePermissionsChange(true)}
                                />Read
                        </Label>
                    </FormGroup>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Update}
                                value=".Update"
                                onChange={this.handlePermissionsChange(true)}
                                />Update
                        </Label>
                    </FormGroup>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={this.state.permissions && permRef.Delete}
                                value=".Delete"
                                onChange={this.handlePermissionsChange(true)}
                                />Delete
                        </Label>
                    </FormGroup>
                </div>
            }
        </React.Fragment>
        );
    }

    private getReferenceFromDotFormat = (baseRef: any, dotFormatName: string) => {
        let returnRef = baseRef;
        const splitName = dotFormatName.split('.');
        for (const name of splitName) {
            returnRef = returnRef[name];
        }
        return returnRef;
    }

    private renderAllOrAssignedPermission = (permName: string, permDescription: string) =>
    {
        const permissionReference = this.getReferenceFromDotFormat(this.state.permissions, permName);
        const basePermissionReference = this.getReferenceFromDotFormat(this.state.basePermissions, permName);
        return (
                <React.Fragment>
                    <FormGroup check={true}>
                        <Label check={true}>
                            <Input
                                type="checkbox"
                                name={permName}
                                checked={basePermissionReference}
                                onChange={this.handleBaseChange}
                                />{permDescription}
                        </Label>
                    </FormGroup>
                    { basePermissionReference && 
                        <div style={{marginLeft: '2rem'}}>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="radio"
                                        name={permName}
                                        checked={this.state.permissions && permissionReference.All}
                                        value=".All"
                                        onChange={this.handlePermissionsChange()}
                                        />All
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="radio"
                                        name={permName}
                                        checked={this.state.permissions && permissionReference.Assigned}
                                        value=".Assigned"
                                        onChange={this.handlePermissionsChange()}
                                        />Assigned
                                </Label>
                            </FormGroup>
                        </div>
                            }
                </React.Fragment>
        );
    }
    public render() {
        const role = this.state.role;
        const roleId = this.props.match.params.id;
        const permissions = this.state.permissions;
        return (
            <div>
                <Row className="header">
                    <Col md="12">
                        {roleId === 'new' ? (
                            <h2 className="float-left">Add New Role</h2>
                        ) : (
                            <h2 className="float-left">Role Detail for {role.name}</h2>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Label>Role Name</Label>
                            <Input
                                type="text"
                                id="name"
                                name="name"
                                value={role.name}
                                onChange={this.handleChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label>Description</Label>
                            <Input
                                type="text"
                                id="description"
                                name="description"
                                value={role.description}
                                onChange={this.handleChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md="6">
                        <h3>User Permissions</h3>
                        <h4 onClick={this.toggleList('wells')}><OpenArrow color={this.TenantStore.iconColor} open={this.state.userPermissionAccordian.wells} />Wells</h4>
                        { this.state.userPermissionAccordian.wells &&
                        <React.Fragment>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells.Read"
                                        checked={this.state.basePermissions.Wells.Read}
                                        onChange={this.handleBaseChange}
                                        />View Wells
                                </Label>
                            </FormGroup>
                            { this.state.basePermissions.Wells.Read && 
                                <div style={{marginLeft: '2rem'}}>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Wells.Read"
                                                checked={permissions && permissions.Wells.Read.All}
                                                value=".All"
                                                onChange={this.handlePermissionsChange()}
                                                />All
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Wells.Read"
                                                checked={permissions && permissions.Wells.Read.Assigned}
                                                value=".Assigned"
                                                onChange={this.handlePermissionsChange()}
                                                />Assigned
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Wells.Read"
                                                checked={permissions && permissions.Wells.Read.Separate}
                                                value=".Separate"
                                                onChange={this.handlePermissionsChange()}
                                                />Separate
                                        </Label>
                                    </FormGroup>
                                </div>
                            
                            }
                            {this.renderAllOrAssignedPermission("Wells.Update", "Update Wells")}
                            {this.renderAllOrAssignedPermission("Wells.ReadClosed", "View Closed Wells")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.ViewArchive}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".ViewArchive"
                                        />View Archived Wells
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.Create}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".Create"
                                        />Create Wells
                                </Label>
                            </FormGroup>
                            {this.renderAllOrAssignedPermission("Wells.Close", "Close Wells")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.Archive}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".Archive"
                                        />Archive Wells
                                </Label>
                            </FormGroup>
                            {this.renderAllOrAssignedPermission("Wells.AddUser", "Add User to Well")}
                            {this.renderAllOrAssignedPermission("Wells.RemoveUser", "Remove User From Wells")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.IsFieldSupervisor}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".IsFieldSupervisor"
                                        />Is Field Supervisor
                                </Label>
                            </FormGroup>
                            {this.renderAllOrAssignedPermission("Wells.ActivateSupervisor", "Activate Supervisor")}
                            {this.renderAllOrAssignedPermission("Wells.DeactivateSupervisor", "Deactivate Supervisor")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.Delete}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".Delete"
                                        />Delete Wells
                                </Label>
                                
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.NotifyNew}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".NotifyNew"
                                        />Receive Notification for New Well
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Wells"
                                        checked={permissions && permissions.Wells.NotifyFinish}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".NotifyFinish"
                                        />Receive Notification for Finishing Status
                                </Label>
                            </FormGroup>
                        </React.Fragment>
                        }
                        <h4 onClick={this.toggleList('wellTests')}><OpenArrow color={this.TenantStore.iconColor} open={this.state.userPermissionAccordian.wellTests} />Well Tests</h4>
                        {
                            this.state.userPermissionAccordian.wellTests && 
                        <React.Fragment>
                            {this.renderAllOrAssignedPermission("WellTests.Read", "View Well Tests")}
                            {this.renderAllOrAssignedPermission("WellTests.Create", "Create Well Tests")}
                            {this.renderAllOrAssignedPermission("WellTests.Update", "Update Well Tests")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="WellTests"
                                        checked={permissions && permissions.WellTests.EditSpecificGravity}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".EditSpecificGravity"
                                        />Edit Specific Gravities
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="WellTests"
                                        checked={permissions && permissions.WellTests.EditIntervalStartDate}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".EditIntervalStartDate"
                                        />Edit Interval Start Dates
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="WellTests"
                                        checked={permissions && permissions.WellTests.EditProductPrice}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".EditProductPrice"
                                        />Edit Product Prices
                                </Label>
                            </FormGroup>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="WellTests"
                                        checked={permissions && permissions.WellTests.EditAdjustedAmount}
                                        onChange={this.handlePermissionsChange(true)}
                                        value=".EditAdjustedAmount"
                                        />Edit Adjusted Amounts
                                </Label>
                            </FormGroup>
                            {this.renderAllOrAssignedPermission("WellTests.Delete", "Delete Well Tests")}
                            {this.renderAllOrAssignedPermission("WellTests.Approve", "Approve Well Tests")}
                        </React.Fragment>
                        }
                        <h4 onClick={this.toggleList('reports')}><OpenArrow color={this.TenantStore.iconColor} open={this.state.userPermissionAccordian.reports} />Reports</h4>
                        {
                            this.state.userPermissionAccordian.reports &&
                        <React.Fragment>
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Reports.System.View"
                                        checked={this.state.basePermissions.Reports.System.View}
                                        onChange={this.handleBaseChange}
                                        />View System Reports
                                </Label>
                            </FormGroup>
                            { this.state.basePermissions.Reports.System.View && 
                                <div style={{marginLeft: '2rem'}}>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Reports.System.View"
                                                checked={permissions && permissions.Reports.System.View.All}
                                                value=".All"
                                                onChange={this.handlePermissionsChange()}
                                                />All
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Reports.System.View"
                                                checked={permissions && permissions.Reports.System.View.Assigned}
                                                value=".Assigned"
                                                onChange={this.handlePermissionsChange()}
                                                />Assigned
                                        </Label>
                                    </FormGroup>
                                </div>
                            }
                            
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Reports.Custom.View"
                                        checked={this.state.basePermissions.Reports.Custom.View}
                                        onChange={this.handleBaseChange}
                                        />View Custom Reports
                                </Label>
                            </FormGroup>
                            { this.state.basePermissions.Reports.Custom.View && 
                                <div style={{marginLeft: '2rem'}}>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Reports.Custom.View"
                                                checked={permissions && permissions.Reports.Custom.View.All}
                                                value=".All"
                                                onChange={this.handlePermissionsChange()}
                                                />All
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check={true}>
                                        <Label check={true}>
                                            <Input
                                                type="radio"
                                                name="Reports.Custom.View"
                                                checked={permissions && permissions.Reports.Custom.View.Assigned}
                                                value=".Assigned"
                                                onChange={this.handlePermissionsChange()}
                                                />Assigned
                                        </Label>
                                    </FormGroup>
                                </div>
                            }
                        </React.Fragment>
                        }
                        <h4 onClick={this.toggleList('dispatch')}><OpenArrow color={this.TenantStore.iconColor} open={this.state.userPermissionAccordian.dispatch} />Dispatches</h4>
                        {
                            this.state.userPermissionAccordian.dispatch &&
                            <React.Fragment>
                                {this.renderAllOrAssignedPermission('Dispatch.Create', 'Create')}
                                {this.renderAllOrAssignedPermission('Dispatch.Read', 'Read')}
                                <FormGroup check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="Dispatch"
                                            checked={permissions && permissions.Dispatch.Comment}
                                            onChange={this.handlePermissionsChange(true)}
                                            value=".Comment"
                                            />Comment
                                    </Label>
                                </FormGroup>
                                {this.renderAllOrAssignedPermission('Dispatch.UpdateSubmitted', 'Update Submitted')}
                                {this.renderAllOrAssignedPermission('Dispatch.UpdateRejected', 'Update Rejected')}
                                {this.renderAllOrAssignedPermission('Dispatch.UpdateApproved', 'Update Approved')}
                                {this.renderAllOrAssignedPermission('Dispatch.Approve', 'Approve')}
                                {this.renderAllOrAssignedPermission('Dispatch.Cancel', 'Cancel')}
                                {this.renderAllOrAssignedPermission('Dispatch.UpdateRTA', 'Update RTA')}
                                <FormGroup check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="Dispatch"
                                            checked={permissions && permissions.Dispatch.Confirm}
                                            onChange={this.handlePermissionsChange(true)}
                                            value=".Confirm"
                                            />Confirm
                                    </Label>
                                </FormGroup>
                                <FormGroup check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="Dispatch"
                                            checked={permissions && permissions.Dispatch.UpdateConfirmed}
                                            onChange={this.handlePermissionsChange(true)}
                                            value=".UpdateConfirmed"
                                            />Update Confirmed
                                    </Label>
                                </FormGroup>
                                <FormGroup check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="Dispatch"
                                            checked={permissions && permissions.Dispatch.Allocate}
                                            onChange={this.handlePermissionsChange(true)}
                                            value=".Allocate"
                                            />Allocate Trucks
                                    </Label>
                                </FormGroup>
                                <FormGroup check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="Dispatch"
                                            checked={permissions && permissions.Dispatch.IsDispatcher}
                                            onChange={this.handlePermissionsChange(true)}
                                            value=".IsDispatcher"
                                            />Is Dispatcher
                                    </Label>
                                </FormGroup>
                                {this.renderDispatchNotificationsPermissions()}
                            </React.Fragment>
                        }
                    </Col>
                    <Col md="6">
                        <h3>Admin Permissions</h3>
                        <FormGroup check={true}>
                            <Label check={true}>
                                <Input
                                    type="checkbox"
                                    name="Admin"
                                    checked={permissions && permissions.Admin.View}
                                    onChange={this.handlePermissionsChange(true)}
                                    value=".View"
                                    />View Admin Dashboard
                            </Label>
                        </FormGroup>
                        {
                            permissions && permissions.Admin.View &&
                        <div style={{marginLeft: '2rem'}}>
                            {this.renderAdminPermission("Admin.Categories", "Categories")}
                            {this.renderAdminPermission("Admin.Contractors", "Contractors")}
                            {this.renderAdminPermission("Admin.Inventory", "Master Retail Lists")}
                            {this.renderAdminPermission("Admin.Regions", "Regions")}
                            {this.renderAdminPermission("Admin.RegionInventory", "Regions Inventory")}
                            {this.renderAdminPermission("Admin.Operators", "Operators")}
                            {this.renderAdminPermission("Admin.Roles", "Roles")}
                            {this.renderAdminPermission("Admin.Users", "Users")}
                            {this.renderAdminPermission("Admin.UnitOfMeasure", "Unit of Measure")}
                            {this.renderAdminPermission("Admin.SolidEquip", "Solids Equipment")}
                            {this.renderAdminPermission("Admin.Warehouses", "Warehouses")}
                            {this.renderAdminPermission("Admin.InternalEmails", "Internal Email Distribution List")}
                            {this.renderAdminPermission("Admin.TaxRates", "Tax Rates")}
                            <FormGroup check={true}>
                                <Label check={true}>
                                    <Input
                                        type="checkbox"
                                        name="Admin.OptionalFieldLists"
                                        checked={permissions && permissions.Admin.OptionalFieldLists.View}
                                        onChange={this.handlePermissionsChange()}
                                        value=".View"
                                        />View Optional Field Lists
                                </Label>
                            </FormGroup>
                            { this.state.permissions.Admin.OptionalFieldLists.View &&
                            <div style={{marginLeft: '2rem'}}>
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.IntervalTypeOptions", "Interval Type Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.MudPropertyOptions", "Mud Property Specification")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.MudTestOptions", "Mud Test Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.StorageTypeOptions", "Storage Type Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.TargetFormationOptions", "Target Formation Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.OilFluidSubTypeOptions", "Oil Fluid Sub-Type Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.WaterFluidSubTypeOptions", "Water Fluid Sub-Type Options")}
                                {this.renderOptionalFieldListPermission("Admin.OptionalFieldLists.ToolsDescriptionOptions", "Tools Description Options")}
                            </div>
                            }
                            
                        </div>
                        }
                    </Col>
                </Row>
                {
                    (this.state.role.id === '' ?
                    this.UserStore.hasPermission('Permissions.Admin.Roles.Create')
                    :
                    this.UserStore.hasPermission('Permissions.Admin.Roles.Update'))
                    && this.state.role.name !== 'SuperAdmin' &&
                    <ButtonRow>
                        <Button color="primary" onClick={this.handleSaveRole} disabled={!this.isRoleComplete()}>Save Role</Button>
                    </ButtonRow>
                }
            </div>
        );
    }
}

export default AdminRoleDetails;


// WEBPACK FOOTER //
// ./src/Components/Main/Admin/Roles/AdminRoleDetails.tsx